import { useNavigate } from "react-router-dom";
import { forwardRef, useCallback, useState } from "react";
import { useAuthCheck } from "../../../../hooks/authHooks/useAuthCheck";
import { useCreateSecret } from "../../../../hooks/secretHooks/useCreateSecret";
import ReceiverEmailInput from "./ReceiverEmailInput";
import SecretKeyInput from "./SecretKeyInput";
import ExpirationSelector from "./ExpirationSelector";
import ActionRow from "./ActionRow";

import {
  invalidReceiverEmail,
  invalidSecretKey,
  invalidSecretText,
} from "../../../../utils/validators/secret.validator";

import type {
  ExpirationTimeOptions,
  ICreateSecretFormData,
} from "../../../../interfaces/secret.interface";
import { useDebounce } from "../../../../hooks/useDebounce";
import { createEncryptedSecret } from "../../../../services/secret.service";
import CharCounter from "./CharCounter";
import CreateSecretButton from "./CreateSecretButton";
import TurnstileWidget from "../../CommonPartials/TurnstileWidget";

const CreateSecretForm = forwardRef<HTMLDivElement>((_, ref) => {
  const { isAuthenticated } = useAuthCheck();
  const navigate = useNavigate();

  const [turnstileToken, setTurnstileToken] = useState<string>("");

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [secretFormData, setSecretFormData] = useState<ICreateSecretFormData>({
    receiverEmail: "",
    secret: "",
    secretKey: "",
    timeTillExpiration: "7d",
  });

  const { mutateAsync: createSecretMutateAsync, isPending: isCreating } =
    useCreateSecret();

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken("");
  }, []);

  const debouncedEmail = useDebounce(secretFormData.receiverEmail, 200);
  const debouncedSecret = useDebounce(secretFormData.secret, 300);
  const debouncedSecretKey = useDebounce(secretFormData.secretKey, 200);
  const charLimit = isAuthenticated ? 10000 : 1000;

  const emailError =
    debouncedEmail.length > 0
      ? invalidReceiverEmail(debouncedEmail)
      : undefined;

  const secretError =
    hasSubmitted || debouncedSecret.length > 0
      ? invalidSecretText(debouncedSecret, charLimit)
      : undefined;

  const secretKeyError =
    debouncedSecretKey.length > 0
      ? invalidSecretKey(debouncedSecretKey)
      : undefined;

  const formHasErrors = emailError || secretKeyError || secretError;

  const handleClear = useCallback((fieldName: string) => {
    setSecretFormData((prev) => ({ ...prev, [fieldName]: "" }));
  }, []);

  const onChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setSecretFormData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const setGeneratedKey = (generatedPassword: string) => {
    setSecretFormData((prev) => ({ ...prev, secretKey: generatedPassword }));
  };

  const onExpirationChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSecretFormData({
      ...secretFormData,
      timeTillExpiration: e.target.value as ExpirationTimeOptions,
    });
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setHasSubmitted(true);

    const emailErr = invalidReceiverEmail(secretFormData.receiverEmail);
    const secretKeyErr = invalidSecretKey(secretFormData.secretKey);
    const secretErr = invalidSecretText(secretFormData.secret, charLimit);

    if (emailErr || secretErr || secretKeyErr) {
      return;
    }

    const secretResponse = await createEncryptedSecret(
      { ...secretFormData, turnstileToken },
      createSecretMutateAsync,
    );

    navigate(`/details/${secretResponse.slug}`, {
      state: {
        secret: {
          ...secretResponse,
        },
      },
    });
  };

  return (
    <div
      className="flex flex-col items-center mb-50 w-140 max-md:max-w-[90vw] noto-sans"
      ref={ref}
    >
      <h2 className="text-3xl mb-8 arvo max-sm:text-2xl">Create Your Secret</h2>
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-3 max-sm:gap-2 max-md:max-w-[90vw]"
      >
        {/* Email */}
        {isAuthenticated && (
          <ReceiverEmailInput
            receiverEmail={secretFormData.receiverEmail}
            error={emailError}
            onChange={onChangeHandler}
            onClear={() =>
              setSecretFormData({ ...secretFormData, receiverEmail: "" })
            }
          />
        )}

        {/* Secret Text Field */}
        <div className="relative">
          <textarea
            name="secret"
            id="secret"
            className={`relative hide-scrollbar resize-none noto-sans w-full h-45 px-4 pt-3 pb-6 text-xs placeholder-(--white) ${secretFormData.secret ? "cursor-auto" : "cursor-pointer"} focus:cursor-auto outline-0 ${secretError ? "input-box-red" : "input-box"}`}
            placeholder="Write your secret here..."
            value={secretFormData.secret}
            onChange={onChangeHandler}
          />
          <CharCounter
            charCount={secretFormData.secret.length}
            charLimit={charLimit}
          />
        </div>

        {/* Secret Key, Expiration Time, Create Button */}
        <div className="flex gap-4 max-sm:gap-2 w-140 max-md:w-[90vw] max-md:flex-col">
          {/* Secret Key */}
          <SecretKeyInput
            secretKey={secretFormData.secretKey}
            error={secretKeyError}
            onChange={onChangeHandler}
            onClear={handleClear}
            setGeneratedKey={setGeneratedKey}
          />

          {/* Expiration Time */}
          <ExpirationSelector
            onChange={onExpirationChangeHandler}
            timeTillExpiration={secretFormData.timeTillExpiration}
          />

          {/* Create Button */}
          <CreateSecretButton
            isCreating={isCreating}
            formHasErrors={formHasErrors}
            hasSubmitted={hasSubmitted}
          />
        </div>
        {secretFormData.secret.length > charLimit && (
          <div className="flex w-full bg-red-400/10 p-2 rounded-sm border border-red-400/10">
            <p className="text-red-400 noto-sans text-sm italic font-light ml-2 mr-2">
              You have exceeded the secret character limit of{" "}
              <span className="font-bold">{charLimit}</span>
              {!isAuthenticated && ". Sign in to increase it."}
            </p>
          </div>
        )}
        {/* Cloudflare Captcha  */}
        {!isAuthenticated && (
          <TurnstileWidget
            onVerify={setTurnstileToken}
            onExpire={handleTurnstileExpire}
            containerClasses="overflow-hidden rounded-lg"
          />
        )}
      </form>

      {/* Sign in, Sigh up features */}
      <ActionRow isAuthenticated={isAuthenticated} />
    </div>
  );
});

export default CreateSecretForm;
